import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { EntityValidationErrorResponse } from '@/modules/onboarding/common/types/entity-validation-error.response';
import { EntityValidationReportResponse } from '@/modules/onboarding/common/types/entity-validation-report.response';
import { GetEntitiesParams } from '@/modules/onboarding/common/types/get-entities.params';
import { axiosInstance } from '@/utils/axios';

import { mapProjectFromDTO } from '../mappers/project.mapper';
import {
  CsvImportProjectsErrorResponse,
  DeleteProjectsBulkRequest,
  MergeProjectsRequest,
  UpdateProjectsBulkRequest,
  UpsertProjectsBulkRequest,
} from '../types';
import { Project } from '../types/projects.types';

export async function getProjects(params: GetEntitiesParams): Promise<PaginatedResponse<Project>> {
  const endpoint = '/api/projects';

  const response = await withApiErrorHandler<PaginatedResponse<any>>(
    endpoint,
    ApiErrorCode.ONBOARDING_PROJECTS_LOAD_FAILED,
    () => axiosInstance.get(endpoint, { params })
  );

  return {
    ...response,
    items: response.items.map(mapProjectFromDTO),
  };
}

export async function upsertProjectsBulk(params: UpsertProjectsBulkRequest): Promise<void> {
  const endpoint = '/api/projects/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_PROJECTS_UPSERT_FAILED, () =>
    axiosInstance.post(endpoint, params)
  );
}

export async function deleteProjects(params: DeleteProjectsBulkRequest): Promise<void> {
  const endpoint = '/api/projects/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_PROJECTS_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint, { data: params })
  );
}

export async function updateProjectsBulk(params: UpdateProjectsBulkRequest): Promise<void> {
  const endpoint = '/api/projects/bulk';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_PROJECTS_UPDATE_FAILED, () =>
    axiosInstance.patch(endpoint, params)
  );
}

export async function getProjectsValidationReport(): Promise<EntityValidationReportResponse> {
  const endpoint = '/api/projects/validation-report';

  return withApiErrorHandler(
    endpoint,
    ApiErrorCode.ONBOARDING_PROJECTS_VALIDATION_REPORT_FAILED,
    () => axiosInstance.get(endpoint)
  );
}

export async function mergeProjects(params: MergeProjectsRequest): Promise<void> {
  const endpoint = '/api/projects/merge';

  await withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_PROJECTS_MERGE_FAILED, () =>
    axiosInstance.post(endpoint, params)
  );
}

export async function submitProjects(): Promise<EntityValidationErrorResponse> {
  const endpoint = '/api/projects/submit';

  return withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_PROJECTS_SUBMIT_FAILED, () =>
    axiosInstance.post(endpoint)
  );
}

export async function importProjectsFromCsv(file: File): Promise<CsvImportProjectsErrorResponse> {
  const endpoint = '/api/projects/import';
  const formData = new FormData();
  // Append the original file directly without recreating it
  // This preserves the file's binary signature for magic number detection
  formData.append('file', file);

  return withApiErrorHandler(endpoint, ApiErrorCode.ONBOARDING_PROJECTS_IMPORT_FAILED, () =>
    axiosInstance.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  );
}
